// MongoDB endpoints - Saha Form Module
// Base URL: http://localhost:8080/api/v1/form
import React, {createContext, useCallback, useState} from 'react';
import axios from 'axios';
import {FORMENDPOINTS, getAuthHeader, JAVA_BACKEND_API_URL, JAVA_BACKEND_USER_PASS} from "../BackendConstants";

const SahaFormContext = createContext();

export const SahaFormProvider = ({children}) => {
    const [activeFormId, setActiveFormId] = useState(null);
    const apiBaseUrl = `${JAVA_BACKEND_API_URL}/form`;

    /**
     * Sahaformları listele (sayfalı)
     * GET /sahaforms?page=1&limit=10&startDate=...&endDate=...
     */
    const fetchSahaForms = async(page = 1, limit = 10, startDate, endDate) => {
        try {
            const response = await axios.get(`${FORMENDPOINTS.sahaformlist}`, {
                params: {
                    page,
                    limit,
                    ...(startDate && { startDate }),
                    ...(endDate && { endDate })
                },
                headers: getAuthHeader()
            });
            return response.data;
        } catch (error) {
            console.error('fetchSahaForms hatası:', error);
            throw error;
        }
    };

    /**
     * Var mı kontrol et, yoksa oluştur (dedup logic)
     * POST /sahaform/checkOrSave
     * Aynı standId + formLevelId + formTypeId varsa döndür, yoksa oluştur
     */
    const checkOrSave = async(standId, formLevelId, formTypeId, firmaId, fuarId, userId) => {
        try {
            const response = await axios.post(`${FORMENDPOINTS.savesahaform}`, {
                standId,
                formLevelId,
                formTypeId,
                firmaId,
                fuarId,
                userId
            },
                {
                    headers: getAuthHeader()
                } );
            // MongoDB döndürülen id'si 'id' field'ında gelir
            if(response.data.id){
                setActiveFormId(response.data.id);
            }
            return response.data;
        } catch (error) {
            console.error('checkOrSave hatası:', error);
            throw error;
        }
    };

    /**
     * Partial update (sadece istenilen field'ları güncelle)
     * POST /sahaFormUpdate/{id}
     */
    const updateSahaForm = async (formId, sahaForm) => {
        try {
            const response = await axios.post(`${apiBaseUrl}/sahaFormUpdate/${formId}`, sahaForm, {
                headers: getAuthHeader()
            });
            return response.data;
        } catch (error) {
            console.error('updateSahaForm hatası:', error);
            throw error;
        }
    };

    /**
     * Form'un tüm cevaplarını getir
     * GET /getContentValues?activeFormId={activeFormId}
     * Her soru (subId) için en son cevabı döndürür
     */
    const getFormValues = async (activeFormId) => {
        if(!activeFormId){
            console.warn('getFormValues: activeFormId boş');
            return [];
        }
        try {
            const response = await axios.get(`${FORMENDPOINTS.getContentValues}`, {
                params: {
                    activeFormId: activeFormId
                },
                headers: getAuthHeader(),
                'X-user-Id': localStorage.getItem('userId') || '',
                'X-token': localStorage.getItem('token') || ''

            });
            return response.data || [];
        } catch (error) {
            console.error('getFormValues hatası:', error);
            return [];
        }
    };

    /**
     * Form cevabı kaydet
     * PUT /saveFormContent
     * Duplicate check: activeFormId + categoryId + subId + answerValue + formLevelId
     */
    const saveFormValue = async (
        activeFormId,
        categoryId,
        subId,
        answerValue,
        formLevelId,
        parentCategoryId = 0,
        sahaFormId = 0,
        relatedForms = null
    ) => {
        if(!activeFormId){
            console.warn('saveFormValue: activeFormId boş');
            return {};
        }
        try {
            const response = await axios.put(`${FORMENDPOINTS.saveformcContent}`, {
                activeFormId,        // MongoDB _id string
                parentCategoryId,
                categoryId,
                subId,
                answerValue,
                formLevelId,
                sahaFormId,
                relatedForms
            },
            {
                headers: getAuthHeader()
            });
            return response.data || {};
        } catch (error) {
            console.error('saveFormValue hatası:', error);
            return {};
        }
    };

    /**
     * Fair'daki aggregated cevaplarını getir (opsiyonel, rapor için)
     * GET /getFormAnswers?fuarId={fuarId}
     */
    const getFormAnswers = async (fuarId) => {
        if(!fuarId){
            console.warn('getFormAnswers: fuarId boş');
            return [];
        }
        try {
            const response = await axios.get(`${FORMENDPOINTS.getFormAnswers}`, {
                params: { fuarId },
                headers: getAuthHeader()
            });
            return response.data || [];
        } catch (error) {
            console.error('getFormAnswers hatası:', error);
            return [];
        }
    };

    /**
     * Seçilen form ait fotoğrafları getir
     * GET /sahaform/{formId}/photos
     */
    const getFormPhotos = async (formId) => {
        if (!formId) {
            console.warn('getFormPhotos: formId boş');
            return [];
        }
        try {
            const response = await axios.get(
                `${FORMENDPOINTS.getFormPhotos}/${formId}/photos`,
                { headers: getAuthHeader() }
            );

            // Fotoğrafa erişilebilir URL ekle
            const photosWithUrls = response.data.map(photo => ({
                ...photo,
                publicUrl: `https://gw.sakagrup.com:8443/public/files/sahaformphotos/${photo.storedFileName}`
            }));

            return photosWithUrls || [];
        } catch (error) {
            console.error('getFormPhotos hatası:', error);
            return [];
        }
    };



    /**
     * Forma fotoğraf yükle
     * POST /sahaform/{formId}/photo?fuarId={fuarId}
     * Basic Auth ile
     */
    const uploadPhoto = async (formId, fuarId, file) => {
        if (!formId || !fuarId || !file) {
            console.warn('uploadPhoto: formId, fuarId veya file boş');
            return null;
        }
        try {
            const photoFormData = new FormData();
            photoFormData.append('file', file);

            // #429: the shared Basic credential (BackendConstants.js), not a copy typed here
            const encodedCredentials = btoa(JAVA_BACKEND_USER_PASS);

            const response = await axios.post(
                `${FORMENDPOINTS.uploadPhoto}/${formId}/photo?fuarId=${fuarId}`,
                photoFormData,
                {
                    headers: {
                        'Authorization': `Basic ${encodedCredentials}`,
                        'X-user-Id': localStorage.getItem('userId') || '',
                        'X-token': localStorage.getItem('token') || ''
                    }
                }
            );
            console.log('✅ Fotoğraf başarıyla yüklendi:', response.data);
            return response.data;
        } catch (error) {
            console.error('⚠️ Fotoğraf yükleme hatası:', error);
            throw error;
        }
    };

    return (
        <SahaFormContext.Provider value={{
            fetchSahaForms,
            checkOrSave,
            updateSahaForm,
            getFormValues,
            saveFormValue,
            getFormAnswers,
            getFormPhotos,
            uploadPhoto,
            activeFormId,
            setActiveFormId
        }}>
            {children}
        </SahaFormContext.Provider>
    );
}

export default SahaFormContext;